/**
 * Shared "label column" resolution for FK targets.
 *
 * A loose FK and a lookup enum both answer the same question about a target
 * table: *which column carries the human-readable name?* The answer is NOT
 * stored on either declaration — it lives once per table in
 * `DatabaseMetaInfo.tableDisplayColumns` so one change updates every
 * relationship pointing at that table.
 *
 * Both the interactive path (`useLookupEnumLabelColumn`, the declare dialog /
 * details dialog dropdown) and the background path (`useLookupLabelSelfHeal`,
 * the miss-driven cache refresh) go through the helpers here so the label
 * column they resolve can never drift apart. The query key is shared with
 * `LooseRelationshipTargetPicker` too, so all three read one cache entry.
 */

import type { DatabaseMetaInfo } from '@/types/electron';
import { pickDisplayColumn } from '../../table-explorer/utils/pickDisplayColumn';
import { resolveTableDisplayColumn } from './tableDisplayColumn';

/** Target-table column lists are schema metadata — cheap to keep for a while. */
export const LOOKUP_TARGET_COLUMNS_STALE_MS = 5 * 60 * 1000;

/**
 * TanStack key for "the column names of this FK target table". Deliberately
 * the same key `LooseRelationshipTargetPicker` and `useLookupEnumLabelColumn`
 * already use — a background self-heal reuses whatever the UI just fetched
 * (and vice versa) instead of issuing its own metadata round-trip.
 */
export const lookupTargetColumnsQueryKey = (
  connectionId: string | null | undefined,
  database: string | null | undefined,
  schema: string | undefined,
  table: string | undefined,
) => ['loose-fk-target-columns', connectionId, database, schema, table] as const;

/** Column names of the FK target table; `[]` when unavailable (never throws). */
export async function fetchLookupTargetColumns(args: {
  connectionId: string | null | undefined;
  database: string | null | undefined;
  schema: string | undefined;
  table: string | undefined;
}): Promise<string[]> {
  const { connectionId, database, schema, table } = args;
  if (!connectionId || !database || table == null) return [];
  if (!window.electronAPI?.getTableMetadata) return [];
  const result = await window.electronAPI.getTableMetadata({
    connectionId,
    database,
    schema: schema ?? '',
    tableName: table,
  });
  if (!result.success || !result.metadata) return [];
  return result.metadata.columns.map((c) => c.name);
}

/**
 * The label column for a target table, resolved in the canonical order:
 *   1. the persisted `tableDisplayColumns` entry (`''` / stale = explicit none),
 *   2. the `pickDisplayColumn` name heuristic over the target's columns.
 *
 * `null` = no label column (keys-only). Callers that let the user override the
 * result (the dialog dropdown) layer their own pick ON TOP of this.
 */
export function resolveLookupLabelColumn(args: {
  metaInfo: DatabaseMetaInfo | null | undefined;
  schema: string | undefined;
  table: string;
  /** The FK key column — never a candidate for labelling itself. */
  keyColumn: string | undefined;
  columns: readonly string[];
}): string | null {
  const { metaInfo, schema, table, keyColumn, columns } = args;
  // `undefined` = never declared for this table → run the heuristic;
  // `null` = explicit none (or a stale entry) → respect it.
  const stored = resolveTableDisplayColumn(metaInfo ?? null, schema ?? '', table, columns);
  if (stored !== undefined) return stored;
  if (columns.length === 0) return null;
  return pickDisplayColumn(columns, keyColumn);
}
